import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Core Advantages page data
// ─────────────────────────────────────────────
const coreAdvantages = [
  {
    icon: 'ri-leaf-line',
    tag: '01',
    title: 'Commitment to Natural Ingredients',
    subtitle: 'Sourcing nutrients from natural food origins — healthy with zero burden',
    desc: 'Kang Yong strictly selects raw materials from natural food sources, avoiding over-refined or synthetic additives so every formula stays true to nature. From ingredient origin to component purity, full transparency lets consumers feel confident — and brands sell with credibility.',
    points: [
      'Rigorous qualification review for natural ingredient suppliers',
      'Pesticide residue & heavy metal test reports for every batch',
      'No unnecessary synthetic additives',
      'Organically certified raw materials available upon request',
    ],
    img: 'https://readdy.ai/api/search-image?query=fresh%20natural%20herbs%20botanical%20ingredients%20turmeric%20ginger%20green%20tea%20extract%20dried%20herbs%20arranged%20beautifully%20on%20white%20marble%20surface%2C%20health%20supplement%20natural%20raw%20materials%2C%20clean%20minimal%20professional%20photography%20with%20soft%20natural%20lighting&width=720&height=480&seq=ca-natural-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-heart-line',
    tag: '02',
    title: 'Creating a Triple Win for Health',
    subtitle: 'Helping clients enter the health market and consumers build lasting wellness',
    desc: 'We believe a great OEM partner can create a triple win: brand clients successfully launch and profit, end consumers receive genuinely effective health products, and Kang Yong continuously refines its expertise. These three are interdependent — none can succeed without the others.',
    points: [
      'Brand clients: lower barriers and risks for entering the health market',
      'Consumers: transparent ingredients, consistent-quality health products',
      'Kang Yong: building expertise and reputation through every partnership',
      'Long-term collaboration, growing and sharing results together',
    ],
    img: 'https://readdy.ai/api/search-image?query=happy%20healthy%20diverse%20group%20of%20people%20holding%20supplement%20products%20with%20green%20nature%20background%2C%20health%20wellness%20teamwork%20collaboration%20concept%2C%20warm%20natural%20lighting%20professional%20photography&width=720&height=480&seq=ca-winwin-01&orientation=landscape',
    reverse: true,
  },
  {
    icon: 'ri-service-line',
    tag: '03',
    title: 'Integrated Professional Service',
    subtitle: 'Six simple steps to realize your ideal product — a low-risk natural health supplement',
    desc: 'From the initial consultation through to market launch, Kang Yong provides a fully integrated one-stop service. No need to coordinate with multiple vendors — one point of contact handles formulation, manufacturing, quality assurance, and packaging, dramatically reducing communication costs and risk.',
    points: [
      'Step 1: Needs consultation & product positioning analysis',
      'Step 2: Formula development & ingredient selection',
      'Step 3: Small-batch trial production & sample confirmation',
      'Step 4: QC acceptance & regulatory review',
      'Step 5: Full-scale production, packaging & delivery',
      'Step 6: Post-launch technical support',
    ],
    img: 'https://readdy.ai/api/search-image?query=professional%20biotech%20team%20meeting%20discussing%20product%20development%20roadmap%20with%20tablet%20laptop%20in%20modern%20clean%20office%2C%20health%20supplement%20company%20collaboration%2C%20natural%20light%20bright%20interior&width=720&height=480&seq=ca-service-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-store-line',
    tag: '04',
    title: 'Small-Batch Orders Welcome',
    subtitle: 'Reduce inventory pressure — the best choice for niche markets',
    desc: 'The biggest barrier for many new or niche-market brands is excessively high minimum order quantities. Kang Yong supports small-batch ordering, letting you validate market demand with minimal inventory risk, confirm product viability, then scale up to full production — growing steadily.',
    points: [
      'Capsule / tablet products: MOQ from 3,000 bottles',
      'Sachet / granule products: MOQ from 5,000 packets',
      'Ideal for new brands testing the market or niche items',
      'Seamless scale-up to mass production after trial success',
    ],
    img: 'https://readdy.ai/api/search-image?query=small%20batch%20supplement%20production%20sample%20bottles%20capsules%20arranged%20neatly%20on%20clean%20white%20table%2C%20minimal%20order%20quantity%20product%20testing%2C%20health%20product%20sampling%20photography&width=720&height=480&seq=ca-moq-01&orientation=landscape',
    reverse: true,
  },
];

// ─────────────────────────────────────────────
// Production page data
// ─────────────────────────────────────────────
const envFeatures = [
  {
    icon: 'ri-building-4-line',
    title: 'Clean Production Facility',
    desc: 'Class 10,000 cleanroom standard with full temperature & humidity monitoring, preventing cross-contamination and ensuring consistent quality every batch.',
  },
  {
    icon: 'ri-flask-line',
    title: 'In-House R&D Laboratory',
    desc: 'Equipped with modern analytical instruments supporting formula development, stability testing, and efficacy verification — quality controlled from the source.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'QC Testing Lab',
    desc: 'Full microbial, heavy metal, and pesticide residue testing equipment; a CoA (Certificate of Analysis) is issued for every batch.',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Temperature-Controlled Storage & Logistics',
    desc: 'Zone-controlled storage according to ingredient and finished product characteristics; FIFO management ensures freshness and product safety.',
  },
];

const qcProcess = [
  { step: '01', icon: 'ri-truck-line', title: 'Incoming Raw Material Inspection', desc: 'Every batch of raw materials undergoes appearance, active content, microbial, and heavy metal testing upon arrival. Any non-conforming batches are returned immediately.' },
  { step: '02', icon: 'ri-settings-3-line', title: 'In-Process Quality Monitoring', desc: 'Key quality indicators such as weight variation, disintegration time, and hardness are monitored throughout production; any anomaly triggers an immediate line stop.' },
  { step: '03', icon: 'ri-eye-line', title: 'Semi-Finished Product Intermediate Control', desc: 'Intermediate quality checks are carried out at each process node; semi-finished products must meet specifications before advancing to the next step.' },
  { step: '04', icon: 'ri-checkbox-circle-line', title: 'Full Finished Product Testing', desc: 'Comprehensive testing before shipment covers microbial analysis, pesticide residues, heavy metals, and labeling compliance.' },
  { step: '05', icon: 'ri-file-check-line', title: 'Certificate of Analysis Issuance', desc: 'A complete Certificate of Analysis (CoA) is issued for each batch, available for client records and market audits.' },
];

const certifications = [
  {
    name: 'GMP Certification',
    fullName: 'Good Manufacturing Practice',
    desc: 'Compliant with Taiwan\'s Food GMP Good Manufacturing Practice standard — production environment, process controls, and quality documentation all meet certification requirements.',
    icon: 'ri-award-line',
    color: '#4CAF50',
  },
  {
    name: 'HACCP Certification',
    fullName: 'Hazard Analysis Critical Control Points',
    desc: 'A systematic hazard analysis process is established, with control standards set at every Critical Control Point (CCP) to proactively prevent food safety hazards.',
    icon: 'ri-scan-line',
    color: '#388e3c',
  },
  {
    name: 'ISO 22000',
    fullName: 'Food Safety Management System',
    desc: 'Certified under the international food safety management system, integrating HACCP principles with ISO management systems to provide internationally-compliant food safety assurance.',
    icon: 'ri-shield-check-line',
    color: '#2e7d32',
  },
  {
    name: 'SGS Third-Party Testing',
    fullName: 'Independent Third-Party Safety Verification',
    desc: 'Regular independent testing commissioned from international agencies such as SGS provides objective data to verify product safety and strengthen brand credibility.',
    icon: 'ri-verified-badge-line',
    color: '#43a047',
  },
];

const docSupport = [
  { icon: 'ri-file-list-3-line', text: 'Batch Production Records' },
  { icon: 'ri-file-check-line', text: 'Certificate of Analysis (CoA)' },
  { icon: 'ri-file-paper-2-line', text: 'Raw Material Specifications' },
  { icon: 'ri-shield-check-line', text: 'Stability Test Reports' },
  { icon: 'ri-global-line', text: 'Export Quality Document Support' },
  { icon: 'ri-building-line', text: 'Factory Audit Cooperation & Documentation' },
];

// ─────────────────────────────────────────────
// Cases page data
// ─────────────────────────────────────────────
const cases = [
  {
    id: 1,
    tag: 'Beauty & Skincare',
    tagColor: '#4CAF50',
    title: 'Hair Growth & Black Hair Jelly Stick Product',
    subtitle: 'Developing a natural botanical functional jelly stick targeting hair health needs',
    img: 'https://readdy.ai/api/search-image?query=hair%20health%20beauty%20supplement%20jelly%20stick%20product%20with%20natural%20plant%20extract%20black%20sesame%20ginger%20ingredients%20on%20clean%20white%20background%20elegant%20packaging%2C%20professional%20product%20photography&width=600&height=400&seq=case-hair-01&orientation=landscape',
    background: 'The client was a hair-care-focused brand looking to develop a jelly stick format that balanced taste and efficacy, targeting hair growth, strengthening, and darkening, aimed at consumers aged 35–60 concerned about hair loss.',
    challenge: 'Natural hair-darkening ingredients (such as black sesame and fo-ti extract) have a strong smell; effective dosage had to be achieved without compromising taste, while the jelly stick format demands high uniformity and stability.',
    solution: 'The Kang Yong R&D team conducted multiple rounds of flavor adjustments, using natural fruit juice to mask bitterness, paired with stabilizers to ensure uniform content per stick; a 6-month accelerated stability test confirmed shelf life.',
    result: 'The finished product passed stability validation. After launching on e-commerce platforms, first-month sales exceeded expectations with a high repurchase rate, and the client has continued placing additional orders to scale up production.',
  },
  {
    id: 2,
    tag: 'Pet Health',
    tagColor: '#388e3c',
    title: 'Domestic & International Pet Health Supplement Series',
    subtitle: 'Natural functional pet health products compliant with feed additive regulations',
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20products%20natural%20organic%20dog%20cat%20vitamins%20treats%20on%20light%20background%2C%20cute%20animal%20care%20products%20with%20herbs%20botanical%20ingredients%2C%20professional%20product%20photography&width=600&height=400&seq=case-pet-02&orientation=landscape',
    background: 'The client was a pet health brand planning to target both the Taiwanese domestic market and Southeast Asian export markets, and needed an OEM manufacturer familiar with pet feed additive regulations.',
    challenge: 'Taiwan\'s pet feed regulations differ from general food regulations; special attention was required for ingredient selection and labeling format. Exporting to certain Southeast Asian markets also required compliance with local regulations and dual-track documentation.',
    solution: 'Kang Yong assigned a dedicated regulatory specialist to confirm ingredient compliance with the client, prepared formulation documentation according to Taiwan and each target export market\'s standards, and assisted with relevant filing procedures.',
    result: 'The product line successfully launched in Taiwan while simultaneously obtaining import licenses for Malaysia and Thailand. It has become the client\'s flagship lineup, now expanded to 6 SKUs.',
  },
  {
    id: 3,
    tag: "Children's Nutrition",
    tagColor: '#2e7d32',
    title: "Children's Nutritional Supplement Powder",
    subtitle: 'Great-tasting, safe-ingredient powder supplement developed for children',
    img: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20powder%20nutrition%20stick%20packet%20colorful%20fruit%20flavored%20with%20vitamins%20minerals%20on%20white%20background%2C%20kids%20health%20food%20product%20photography&width=600&height=400&seq=case-kids-01&orientation=landscape',
    background: 'The client was a children\'s health brand seeking to develop a compound vitamin and mineral powder: easy to dissolve in water, naturally sweet-tasting, free from artificial coloring, and suitable for daily use by children aged 3–12.',
    challenge: 'Children\'s products require the highest ingredient safety standards — every ingredient had to be confirmed suitable for children. The powder format also needed good dispersibility and solubility to prevent sediment affecting taste.',
    solution: 'Child-safe-grade raw materials were strictly selected and submitted to a third-party agency for both heavy metal and microbial testing. Natural fruit juice powder was used to develop the flavor profile, confirmed through multiple taste tests targeting the intended age group.',
    result: 'The product passed food safety inspections and successfully launched through brick-and-mortar pharmacies and e-commerce channels. It was endorsed by multiple parenting bloggers and became the client brand\'s star product.',
  },
  {
    id: 4,
    tag: 'Radio Station Co-Brand',
    tagColor: '#43a047',
    title: 'High-Quality Fish Oil for a Radio Station Brand',
    subtitle: 'Partnering with a well-known broadcast media brand to create a high-purity Omega-3 fish oil series',
    img: 'https://readdy.ai/api/search-image?query=premium%20omega3%20fish%20oil%20capsules%20high%20purity%20supplement%20bottle%20with%20ocean%20sea%20background%2C%20high%20quality%20health%20product%20professional%20photography%20clean%20white%20minimal&width=600&height=400&seq=case-fishoil-01&orientation=landscape',
    background: 'A well-known radio station, whose long-time listeners have high health awareness, decided to launch its own health supplement brand. The flagship product was a high-quality fish oil requiring high-purity EPA/DHA and verified heavy metal safety.',
    challenge: 'The station\'s high profile demanded strict quality requirements and a manufacturing partner capable of providing complete third-party test reports. Production also needed to align with the media outlet\'s program schedule so that the first batch arrived on time.',
    solution: 'TG-form high-concentration fish oil raw material with TG-form Omega-3 content exceeding 85% was provided. SGS was commissioned for complete heavy metal and environmental contaminant testing, with production scheduling flexibly adjusted to match the station\'s timeline.',
    result: 'The product launched on schedule; in-program promotion generated strong sales, the first batch sold out rapidly, and a quick follow-on order was placed — making it the most successful private-label project in the station\'s collaboration history.',
  },
  {
    id: 5,
    tag: 'Agriculture Co-Brand',
    tagColor: '#1b5e20',
    title: 'Health Supplement Series Featuring Agricultural Specialties',
    subtitle: 'Transforming local agricultural ingredients into high-value health products to create a regionally distinctive brand',
    img: 'https://readdy.ai/api/search-image?query=local%20agriculture%20specialty%20health%20supplement%20Taiwan%20indigenous%20ingredients%20taro%20sweet%20potato%20rice%20extract%20natural%20products%20on%20wooden%20table%2C%20farm%20to%20table%20health%20food%20concept%20photography&width=600&height=400&seq=case-agri-01&orientation=landscape',
    background: 'The client was a food brand combining local agriculture that wanted to transform Taiwan\'s distinctive agricultural produce (such as specific rice varieties, tea leaves, and turmeric) into high-value health products and strengthen the regional brand image.',
    challenge: 'Agricultural ingredient content varies by harvest season and origin, requiring standardized raw material specifications. Health claims for local ingredients also had to comply with food labeling regulations without implying exaggerated therapeutic effects.',
    solution: 'Kang Yong helped the client establish raw material specification sheets and set stable procurement standards with the agricultural partners. The R&D team designed formulas based on the characteristics of the agricultural produce, and assisted in drafting health claims compliant with regulatory requirements.',
    result: 'The product series launched successfully. Combined with the agricultural brand\'s storytelling, it became a popular regional souvenir gift while also breaking into chain supermarket health product channels — successfully multiplying the added value of the agricultural ingredients by 3–5×.',
  },
];

// ─────────────────────────────────────────────
// Regulation page data
// ─────────────────────────────────────────────
const whyPoints = [
  {
    icon: 'ri-alert-line',
    title: 'Serious Penalties for Violations',
    desc: 'Violating health food labeling or advertising regulations can result in fines of over one million NTD, mandatory product recall, and severe damage to brand reputation.',
  },
  {
    icon: 'ri-store-line',
    title: 'Channel Listing Requirements',
    desc: 'Chain pharmacies, supermarkets, and e-commerce platforms all require complete compliance documentation when reviewing products — non-compliance means no listing.',
  },
  {
    icon: 'ri-global-line',
    title: 'Different Regulations in Every Export Market',
    desc: 'Different countries have different rules for health food classification, ingredient safety, and labeling format. Each target market must be verified before export.',
  },
  {
    icon: 'ri-time-line',
    title: 'Frequent Regulatory Changes',
    desc: 'Taiwan\'s TFDA regularly updates positive ingredient lists, labeling standards, and advertising review criteria. Failing to keep up may render existing products non-compliant.',
  },
];

const keyRegulations = [
  {
    title: 'Health Food Regulations',
    items: [
      'Conditions and process for Health Food certification (Green Man label)',
      'Restrictions on claiming specific health benefits for general food',
      'Positive ingredient list: which ingredients may or may not be used',
      'Daily intake limits and safety assessment requirements',
    ],
  },
  {
    title: 'Labeling & Advertising Standards',
    items: [
      'Label format for product name, ingredients, and additives',
      'Permissible scope of claims (no disease prevention or treatment claims)',
      'Pre-publication review procedure for advertisements',
      'Regulatory definition of ads involving social media and KOL collaborations',
    ],
  },
  {
    title: 'Production & QC Regulations',
    items: [
      'Applicable scope of GMP Good Manufacturing Practice',
      'HACCP hazard analysis establishment requirements',
      'Batch record and traceability system regulations',
      'Shelf life testing and labeling methods',
    ],
  },
  {
    title: 'Export Regulatory Differences',
    items: [
      'Southeast Asia (Malaysia, Thailand, Vietnam) import regulations',
      'China health food (Blue Hat) application process',
      'US FDA Dietary Supplement (DSHEA) regulations',
      'EU Novel Food application',
    ],
  },
];

const howWeHelp = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: 'Pre-Launch Regulatory Review',
    desc: 'Before formulas are finalized, our regulatory team conducts an ingredient compliance review to confirm all components comply with TFDA positive lists and dosage limits — eliminating regulatory risk from the source.',
  },
  {
    step: '02',
    icon: 'ri-file-list-3-line',
    title: 'Labeling Document Assistance',
    desc: 'We provide label format recommendations and copy review services, helping clients comply in product naming, ingredient labeling, and claim wording — preventing advertising violations.',
  },
  {
    step: '03',
    icon: 'ri-building-2-line',
    title: 'Health Food Application Support',
    desc: 'For clients wishing to apply for Health Food certification (Green Man label), Kang Yong can assist in preparing the scientific documents and test data needed for safety and efficacy assessments.',
  },
  {
    step: '04',
    icon: 'ri-global-line',
    title: 'Export Regulatory Consulting',
    desc: 'For clients planning to export, we provide target market regulatory gap analysis and assist in preparing import documentation and ingredient declaration materials for each market.',
  },
];

const faqs = [
  {
    q: 'What are the regulatory differences between general food and health food?',
    a: 'General food may only use functional wording claiming to "maintain health" and may not claim specific health benefits. Health food (Green Man label), having passed TFDA review, can legally claim specific benefits such as "helps improve blood lipids." The application threshold, cost, and review timeline differ greatly between the two.',
  },
  {
    q: 'What wording in my product copy needs special attention?',
    a: 'Taiwan regulations explicitly prohibit general food from using language related to "disease prevention, treatment, mitigation, or diagnosis" — for example, "lowers blood pressure," "treats insomnia," or "improves diabetes." Compliant alternatives include "helps maintain normal metabolism" or "helps adjust physical constitution."',
  },
  {
    q: 'Can all imported ingredients be used?',
    a: 'Not all imported ingredients are legally usable. The TFDA publishes a "Compilation of Raw Materials Permitted for Use in Food" positive list; ingredients not listed require a safety assessment submission before use. Kang Yong confirms the compliance of all ingredients before formula development.',
  },
  {
    q: 'What are the regulations for social media advertising?',
    a: 'Under the Act Governing Food Safety and Sanitation, online and social media advertisements are subject to the same food advertising regulations. When collaborating with KOLs or influencers, any mention of specific benefits or usage experiences is considered an advertisement — brands and publishers may both be penalized if claims are exaggerated or misleading.',
  },
  {
    q: 'Does exporting to Southeast Asia require additional certifications?',
    a: 'Yes, each country has different requirements. Malaysia requires an import permit application to KKM; Thailand requires registration with Thailand FDA; Vietnam requires an import authorization letter from the Ministry of Health. Kang Yong has experience assisting clients in preparing these documents and can provide initial consultation.',
  },
];

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function EnAboutSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/en/about/core-advantages': {
      title: 'Core Advantages | Natural Sourcing & Low MOQ OEM | Kang Yong Biotech',
      description: 'Four core advantages of Kang Yong Biotech: natural ingredient sourcing, triple-win approach, integrated professional service, and small-batch ordering — helping brands enter the health supplement market.',
      keywords: 'core advantages, natural ingredient sourcing, low MOQ, small batch OEM, triple win partnership',
    },
    '/en/about/production': {
      title: 'Professional Production | GMP Facility & 5-Stage QC | Kang Yong Biotech',
      description: 'Kang Yong Biotech GMP-certified production environment: cleanroom, R&D lab, QC testing lab, temperature-controlled warehouse. Five-stage quality management for every batch.',
      keywords: 'GMP facility, professional production, quality control, cleanroom, health supplement manufacturing, ISO 22000',
    },
    '/en/about/cases': {
      title: 'Success Stories | OEM ODM Brand Collaboration Cases | Kang Yong Biotech',
      description: 'Kang Yong Biotech OEM success stories: beauty skincare, pet health, children nutrition, radio co-brand, agriculture co-brand — diverse product categories with proven manufacturing results.',
      keywords: 'success stories, OEM cases, ODM cases, health supplement manufacturing cases, brand collaboration',
    },
    '/en/about/regulation': {
      title: 'Regulatory Risk Management | Health Supplement Compliance | Kang Yong Biotech',
      description: 'Kang Yong Biotech regulatory risk management: pre-launch compliance review, labeling assistance, health food certification support, and export regulatory consulting for health supplements.',
      keywords: 'health supplement regulation, labeling compliance, food safety regulation, TFDA, health food certification, export regulation',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/en/about/core-advantages'];
  useSEO({ ...currentSeo, lang: 'en', canonicalPath: path });

  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const subNavItems = [
    { path: '/en/about/core-advantages', label: 'Core Advantages', desc: 'Natural ingredients, triple win, integrated service & small-batch ordering — four key advantages.' },
    { path: '/en/about/production', label: 'Professional Production', desc: 'GMP facility, five-stage QC and international certifications for your brand.' },
    { path: '/en/about/cases', label: 'Success Stories', desc: 'Multi-category full-service OEM case studies — see how we help brands launch.' },
    { path: '/en/about/regulation', label: 'Regulatory Risk', desc: 'Understand key health food regulations; we help your brand start on solid compliance ground.' },
  ];

  return (
    <EnLayout>
      {/* ── CORE ADVANTAGES ── */}
      {path === '/en/about/core-advantages' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=lush%20green%20botanical%20garden%20overhead%20view%20with%20diverse%20natural%20plants%20herbs%20growing%2C%20vibrant%20emerald%20green%20nature%20background%2C%20health%20supplement%20brand%20identity%2C%20serene%20clean%20natural%20environment&width=1920&height=420&seq=ca-hero-bg-01&orientation=landscape"
                alt="Core Advantages"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/about" className="hover:text-white cursor-pointer">About Kang Yong</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Core Advantages</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">About Kang Yong</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Core Advantages</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  Natural ingredient sourcing, a triple-win approach, integrated professional service, and small-batch ordering — helping brands confidently enter the health supplement market.
                </p>
              </div>
            </div>
          </section>

          {/* Advantage blocks */}
          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-14 md:space-y-24">
              {coreAdvantages.map((adv) => (
                <div
                  key={adv.tag}
                  className={`flex flex-col ${adv.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}
                >
                  <div className="w-full lg:w-1/2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[#4CAF50] font-bold text-[2rem] leading-none opacity-20">{adv.tag}</span>
                      <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl border border-green-200">
                        <i className={`${adv.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] leading-tight mb-2">{adv.title}</h2>
                    <p className="text-[#4CAF50] font-medium text-[1rem] mb-5">{adv.subtitle}</p>
                    <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{adv.desc}</p>
                    <ul className="space-y-3">
                      {adv.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-2xl overflow-hidden">
                      <img src={adv.img} alt={adv.title} className="w-full h-[300px] lg:h-[360px] object-cover object-top" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Start Collaborating</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-4">Want These Advantages Working for You?</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                Contact the Kang Yong sales team — we will tailor the most suitable ODM partnership plan for your brand.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Inquire About ODM Collaboration
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/en/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Learn About the Cooperation Process
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More in About Kang Yong</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Professional Production', desc: 'GMP facility, five-stage QC and international certifications — a solid production guarantee for your brand.', href: '/en/about/production' },
                  { title: 'Success Stories', desc: 'Multi-category full-service OEM case studies — see how Kang Yong helps brands launch.', href: '/en/about/cases' },
                  { title: 'Cooperation Process', desc: 'From needs alignment to delivery — a clear view of every step in the partnership.', href: '/en/cooperation/process' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── PROFESSIONAL PRODUCTION ── */}
      {path === '/en/about/production' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20GMP%20certified%20pharmaceutical%20manufacturing%20production%20line%20stainless%20steel%20equipment%20workers%20white%20uniform%20hairnet%20clean%20room%2C%20professional%20health%20supplement%20factory%20interior%20bright%20lighting&width=1920&height=420&seq=prod-hero-bg-01&orientation=landscape"
                alt="Professional Production"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/about" className="hover:text-white cursor-pointer">About Kang Yong</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Professional Production</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">About Kang Yong</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Professional Production</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  With a stable production process, quality management, and food safety certifications, we help brands create reliable, trustworthy health products.
                </p>
              </div>
            </div>
          </section>

          {/* Production environment */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Facility</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Professional Production Environment</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  Kang Yong's facility meets GMP standard requirements, providing the safest production environment for every product
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
                {envFeatures.map((f) => (
                  <div key={f.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                      <i className={`${f.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{f.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20clean%20pharmaceutical%20supplement%20manufacturing%20facility%20with%20modern%20equipment%20conveyors%20stainless%20steel%20tanks%20workers%20in%20white%20uniforms%2C%20GMP%20factory%20interior%20bright%20professional%20environment&width=1400&height=500&seq=prod-facility-wide-01&orientation=landscape"
                  alt="Kang Yong Facility Overview"
                  className="w-full h-[320px] md:h-[420px] object-cover object-center"
                />
              </div>
            </div>
          </section>

          {/* QC Process */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">QC Process</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Five-Stage Quality Management Process</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  From raw material intake to finished product shipment, every step is safeguarded by strict quality control
                </p>
              </div>
              <div className="relative">
                <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-green-100 z-0" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
                  {qcProcess.map((q) => (
                    <div key={q.step} className="flex flex-col items-center text-center">
                      <div className="relative mb-5">
                        <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full border-2 border-[#4CAF50]">
                          <i className={`${q.icon} text-[#4CAF50] text-2xl`} />
                        </div>
                        <span className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{q.step}</span>
                      </div>
                      <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{q.title}</h3>
                      <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{q.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Certifications</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Internationally Recognized Quality Certifications</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  HACCP, ISO 22000, GMP and more — multiple certifications are the strongest endorsement of Kang Yong's quality commitment
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certifications.map((cert) => (
                  <div key={cert.name} className="flex gap-5 p-7 bg-[#f7faf7] rounded-2xl border border-gray-100 hover:border-green-200 transition-all duration-300">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${cert.color}15` }}>
                      <i className={`${cert.icon} text-2xl`} style={{ color: cert.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{cert.name}</h3>
                        <span className="text-gray-400 text-[0.75rem]">/ {cert.fullName}</span>
                      </div>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{cert.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Document support */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Document Support</span>
                  <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                    Comprehensive Production Certificates<br />& Quality Document Support
                  </h2>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                    Kang Yong provides complete quality document support, helping clients handle market audits, channel listing reviews, and export customs requirements — so your brand can reach further and stand stronger.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {docSupport.map((d) => (
                      <div key={d.text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <i className={`${d.icon} text-[#4CAF50] text-lg`} />
                        </div>
                        <span className="text-[#1a2e1a] text-[0.875rem] font-medium">{d.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=quality%20control%20manager%20reviewing%20product%20batch%20records%20certificates%20documents%20in%20clean%20pharmaceutical%20laboratory%20office%2C%20professional%20QA%20QC%20documentation%20management%20biotech%20company&width=720&height=480&seq=prod-doc-01&orientation=landscape"
                      alt="Quality Document Support"
                      className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ODM/OEM CTA */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-[#f0faf0] border border-green-100 rounded-3xl px-8 lg:px-14 py-12 text-center">
                <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Suitable for ODM / OEM</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                  Let Kang Yong's Professional Production Capabilities<br />Become the Solid Backbone of Your Brand
                </h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                  Whether it's ODM custom formula contract manufacturing or OEM specified formula production, Kang Yong completes every commission to the highest quality standards.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    Inquire About ODM / OEM Collaboration
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <Link to="/en/about/core-advantages" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    Learn About Core Advantages
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More in About Kang Yong</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Core Advantages', desc: 'Natural ingredients, triple win, integrated service & small-batch ordering — four key advantages explained.', href: '/en/about/core-advantages' },
                  { title: 'Regulatory Risk', desc: 'Understand key health food regulations; Kang Yong helps you start on solid compliance ground.', href: '/en/about/regulation' },
                  { title: 'Contact Us', desc: 'Fill in the inquiry form to learn how Kang Yong can provide production support for your brand.', href: '/en/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── SUCCESS STORIES ── */}
      {path === '/en/about/cases' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20collection%20of%20health%20supplement%20products%20bottles%20capsules%20powders%20lined%20up%20on%20clean%20white%20background%2C%20professional%20health%20brand%20showcase%20product%20photography%2C%20natural%20light&width=1920&height=420&seq=cases-hero-bg-01&orientation=landscape"
                alt="Success Stories"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/about" className="hover:text-white cursor-pointer">About Kang Yong</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Success Stories</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">About Kang Yong</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Success Stories</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  Diverse product categories, rich OEM experience — the following are representative collaboration cases from Kang Yong Biotech.
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 bg-white border-b border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { num: '250+', label: 'Service Brand Count' },
                  { num: '3,200+', label: 'Cumulative OEM Products' },
                  { num: '6+', label: 'Production Dosage Forms' },
                  { num: '10+', label: 'Export Target Markets' },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-[#4CAF50] font-bold text-[2rem] md:text-[2.5rem] leading-none mb-1">{s.num}</p>
                    <p className="text-gray-500 text-[0.875rem]">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cases list */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">OEM Cases</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Featured Collaboration Cases</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  Click on a case to expand detailed information about the collaboration background, challenges, and results
                </p>
              </div>
              <div className="space-y-5">
                {cases.map((c) => (
                  <div
                    key={c.id}
                    className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${expandedCase === c.id ? 'border-[#4CAF50]/40' : 'border-gray-100 hover:border-green-200'}`}
                  >
                    <button
                      className="w-full text-left cursor-pointer"
                      onClick={() => setExpandedCase(expandedCase === c.id ? null : c.id)}
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 p-6">
                        <div className="w-full sm:w-[160px] h-[120px] sm:h-[100px] flex-shrink-0 rounded-xl overflow-hidden">
                          <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="inline-block text-[0.75rem] font-medium px-3 py-1 rounded-full text-white" style={{ backgroundColor: c.tagColor }}>{c.tag}</span>
                            <span className="text-gray-300 text-xs">Case 0{c.id}</span>
                          </div>
                          <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-1">{c.title}</h3>
                          <p className="text-gray-500 text-[0.875rem] leading-[1.6] line-clamp-2">{c.subtitle}</p>
                        </div>
                        <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${expandedCase === c.id ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                          <i className={`text-sm ${expandedCase === c.id ? 'ri-subtract-line' : 'ri-add-line'}`} />
                        </div>
                      </div>
                    </button>
                    {expandedCase === c.id && (
                      <div className="px-6 pb-7 border-t border-gray-100 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {[
                            { label: 'Background', icon: 'ri-information-line', content: c.background },
                            { label: 'Key Challenges', icon: 'ri-error-warning-line', content: c.challenge },
                            { label: 'How Kang Yong Helped', icon: 'ri-tools-line', content: c.solution },
                            { label: 'Results', icon: 'ri-trophy-line', content: c.result },
                          ].map((block) => (
                            <div key={block.label} className="bg-[#f7faf7] rounded-xl p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-7 h-7 flex items-center justify-center">
                                  <i className={`${block.icon} text-[#4CAF50] text-base`} />
                                </div>
                                <h4 className="text-[#1a2e1a] font-bold text-[0.9375rem]">{block.label}</h4>
                              </div>
                              <p className="text-gray-600 text-[0.875rem] leading-[1.75]">{block.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                Your Brand Could Be Our Next Success Story
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                Whatever the size of your product idea, Kang Yong Biotech is committed to taking every opportunity seriously.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Contact Us to Start Collaborating
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/en/services" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  View OEM Services
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More in About Kang Yong</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Core Advantages', desc: 'Learn about Kang Yong\'s four core advantages: natural ingredients, triple win, integrated service, and small-batch orders.', href: '/en/about/core-advantages' },
                  { title: 'Cooperation Process', desc: 'From needs alignment to delivery — a clear view of every step in the partnership.', href: '/en/cooperation/process' },
                  { title: 'Inquiry', desc: 'Fill in the inquiry form — become the next brand Kang Yong helps launch.', href: '/en/cooperation/inquiry' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── REGULATORY RISK ── */}
      {path === '/en/about/regulation' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20legal%20document%20review%20compliance%20health%20supplement%20regulation%20papers%20on%20clean%20desk%20with%20pen%20calculator%2C%20food%20safety%20regulatory%20affairs%20concept%2C%20clean%20minimal%20office%20environment&width=1920&height=420&seq=reg-hero-bg-01&orientation=landscape"
                alt="Regulatory Risk"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/68 to-[#1a3a1a]/30" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/about" className="hover:text-white cursor-pointer">About Kang Yong</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Regulatory Risk</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">About Kang Yong</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Regulatory Risk</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  Regulatory review and risk management before product launch are critical foundations for a brand's long-term stable operation.
                </p>
              </div>
            </div>
          </section>

          {/* Why regulation matters */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Why Regulation Must Come First</span>
                  <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-5">
                    Regulation Isn't a "Back-End Problem" —<br />It Belongs at the Very Front of Your Product
                  </h2>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                    Many brands discover regulatory issues only after product development is complete, or even after launch — at best requiring label revisions, at worst mandatory recall. All prior investment goes to waste. Making regulatory review the very first step in product development ensures every resource is spent where it counts.
                  </p>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                    At the start of every collaboration, Kang Yong conducts a pre-launch regulatory review to confirm that the product direction, ingredient selection, and claim wording comply with regulations before entering production — so your brand starts on solid compliance ground from day one.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {whyPoints.map((p) => (
                      <div key={p.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                          <i className={`${p.icon} text-[#4CAF50] text-base`} />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-0.5">{p.title}</p>
                          <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{p.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=regulatory%20affairs%20specialist%20reviewing%20food%20supplement%20compliance%20documents%20checking%20ingredient%20list%20with%20magnifying%20glass%20in%20office%2C%20professional%20health%20product%20regulatory%20review%20process&width=720&height=500&seq=reg-why-01&orientation=landscape"
                      alt="Regulatory Review"
                      className="w-full h-[340px] lg:h-[420px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key regulations */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Key Regulations</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Essential Regulations for Health Supplement Brands</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  The following covers the most common regulatory aspects brands encounter when planning products
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {keyRegulations.map((reg, idx) => (
                  <div key={reg.title} className="bg-white rounded-2xl p-7 border border-gray-100">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">0{idx + 1}</span>
                      <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{reg.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {reg.items.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-base" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How we help */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">How Kang Yong Helps</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">How Kang Yong Helps You Avoid Regulatory Red Lines</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  From formula design to advertising copy, Kang Yong provides regulatory safeguards at every key checkpoint
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {howWeHelp.map((h) => (
                  <div key={h.step} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-20 leading-none">{h.step}</span>
                      <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100">
                        <i className={`${h.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-3">{h.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{h.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand guarantee */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  <div className="w-full lg:w-1/2">
                    <img
                      src="https://readdy.ai/api/search-image?query=business%20team%20shaking%20hands%20successful%20partnership%20agreement%20health%20supplement%20brand%20collaboration%2C%20confident%20professional%20people%20in%20clean%20bright%20modern%20office%2C%20trust%20partnership%20concept&width=720&height=460&seq=reg-guarantee-01&orientation=landscape"
                      alt="Kang Yong Risk Guarantee"
                      className="w-full h-[280px] lg:h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-12">
                    <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Brand Guarantee</span>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                      What Can Kang Yong's<br />Regulatory Support Bring Your Brand?
                    </h2>
                    <ul className="space-y-4">
                      {[
                        { icon: 'ri-shield-check-line', text: 'Pre-launch compliance confirmed, reducing risk of regulatory penalties' },
                        { icon: 'ri-store-line', text: 'Meets channel audit standards for smooth listing approval' },
                        { icon: 'ri-global-line', text: 'Export documentation ready, improving overseas market entry efficiency' },
                        { icon: 'ri-time-line', text: 'Real-time regulatory update notifications to keep products compliant' },
                        { icon: 'ri-hand-coin-line', text: 'Avoid de-listing losses and recall costs caused by regulatory issues' },
                        { icon: 'ri-award-line', text: 'Complete quality documentation enhances brand credibility with consumers' },
                      ].map((item) => (
                        <li key={item.text} className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className={`${item.icon} text-[#4CAF50] text-base`} />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-12">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">FAQ</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Regulatory Frequently Asked Questions</h2>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className={`bg-[#f7faf7] rounded-xl border transition-all duration-200 overflow-hidden ${openFaq === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}
                  >
                    <button
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaq === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaq === idx ? 'bg-[#4CAF50] text-white' : 'bg-white text-[#4CAF50]'}`}>
                        <i className={`text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </div>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Start Collaborating</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
                Let Kang Yong Help You Prepare Regulatory Compliance<br />for a Confident Launch and Long-Term Operation
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                Contact Kang Yong Biotech — we will conduct a preliminary regulatory feasibility assessment to help your brand get on the right track from the very start.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Consult on Regulatory Feasibility
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/en/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Learn About Cooperation Process
                </Link>
              </div>
            </div>
          </section>

          {/* Related pages */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More in About Kang Yong</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Partnership Services', desc: 'Regulatory risk management, marketing copy, imported ingredient sourcing — learn about Kang Yong\'s full-service offerings.', href: '/en/cooperation/services' },
                  { title: 'Packaging', desc: 'Packaging material selection, visual design, and regulatory labeling — all-in-one packaging solution.', href: '/en/services/packaging' },
                  { title: 'Contact Us', desc: 'Fill in the inquiry form — let Kang Yong assist with a preliminary regulatory feasibility assessment.', href: '/en/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FALLBACK: Sub-nav if path not matched ── */}
      {!['en/about/core-advantages', 'en/about/production', 'en/about/cases', 'en/about/regulation'].some(p => path.includes(p)) && (
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {subNavItems.map((item) => (
                <Link key={item.path} to={item.path} className="bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer block">
                  <p className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.label}</p>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </EnLayout>
  );
}
